import { isSameDay } from 'date-fns';
import { CreateBookkeepingAdjustmentPayload } from '~/types/general-bookkeeping';
import { VATEntryBookkeepingAdjustmentProposal } from '~/types/vat-bookkeeping';

/**
 * Converts a backend proposal adjustment (scrittura di assestamento) into the
 * writable `bookkeeping_adjustment` create payload, forwarding the backend value
 * verbatim (no client re-derivation).
 *
 * Returns null when the adjustment is partial — any null account slot (unresolved
 * item or unconfigured company account) — so the caller surfaces the missing
 * account and falls back to client derivation rather than submitting a blank one.
 */
export function proposalAdjustmentToCreatePayload(
  adjustment: VATEntryBookkeepingAdjustmentProposal,
): CreateBookkeepingAdjustmentPayload | null {
  if (!adjustment.bookkeepingAccountId) return null;

  const bookkeepingTransactions = [];
  for (const tx of adjustment.bookkeepingTransactions) {
    const bookkeepingEntries = [];
    for (const entry of tx.bookkeepingEntries) {
      if (!entry.bookkeepingAccountId) return null;
      bookkeepingEntries.push({
        bookkeepingAccountId: entry.bookkeepingAccountId,
        amount: entry.amount,
        source: entry.source,
      });
    }
    bookkeepingTransactions.push({
      date: tx.date,
      description: tx.description,
      bookkeepingEntries,
      transactionIds: [],
    });
  }

  return {
    adjustmentType: adjustment.adjustmentType,
    sourceType: adjustment.sourceType,
    bookkeepingAccountId: adjustment.bookkeepingAccountId,
    bookkeepingTransactions,
  };
}

export interface ResolvedBackendAdjustment {
  adjustmentAccountId: string | null;
  proposalAdjustment: CreateBookkeepingAdjustmentPayload | null;
  proposalRegistrationDate: Date | null;
}

export function resolveBackendAdjustment(
  backendAdjustment: VATEntryBookkeepingAdjustmentProposal | null | undefined,
  registrationDate: Date | null,
): ResolvedBackendAdjustment | null {
  if (!backendAdjustment) return null;
  const proposalAdjustment = proposalAdjustmentToCreatePayload(backendAdjustment);
  return {
    // A partial adjustment (any null account slot) yields no forwardable payload
    adjustmentAccountId: proposalAdjustment ? backendAdjustment.bookkeepingAccountId : null,
    proposalAdjustment,
    proposalRegistrationDate: registrationDate,
  };
}

/**
 * True when a form entry's forwarded backend adjustment is still authoritative:
 * it exists and the form registration date still equals the proposed one. Editing
 * the registration date hands ownership back to the client fallback.
 */
export function canForwardProposalAdjustment(
  proposalAdjustment: CreateBookkeepingAdjustmentPayload | null | undefined,
  proposalRegistrationDate: Date | null | undefined,
  registrationDate: Date,
): boolean {
  return !!proposalAdjustment && !!proposalRegistrationDate && isSameDay(proposalRegistrationDate, registrationDate);
}
